import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2 } from 'lucide-react';

const StaffLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { staffLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await staffLogin(email, password);

    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error || 'Invalid email or password');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md border-4 border-[#FF6A00] shadow-[0_0_40px_rgba(255,106,0,0.3)]">
        <CardHeader className="space-y-1 text-center bg-gradient-to-b from-black to-gray-900 text-white rounded-t-lg border-b-4 border-[#FF6A00] pb-8">
          <div className="mb-6 flex flex-col items-center pt-4">
            <div className="relative mb-4">
              <div className="absolute inset-0 bg-[#FF6A00] rounded-full blur-2xl opacity-40 scale-110"></div>
              <div className="relative bg-white rounded-full p-3 border-4 border-[#FF6A00] shadow-[0_0_30px_rgba(255,106,0,0.6)]">
                <img
                  src="/images/just-protein-logo.png"
                  alt="Just Protein Logo"
                  className="h-32 w-32 object-contain"
                />
              </div>
            </div>
            <h1 className="text-5xl font-extrabold text-white tracking-wide mb-2">
              Just Protein
            </h1>
            <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#FF6A00] to-transparent mx-auto mt-3"></div>
          </div>
          <CardTitle className="text-2xl text-white font-bold pt-2">Staff Portal</CardTitle>
        </CardHeader>
        <CardContent className="bg-white pt-6">
          {error && (
            <Alert className="mb-4 bg-red-50 text-red-800 border-red-300" variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-black font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-white text-black border-2 border-gray-300 focus:border-[#FF6A00] focus:ring-[#FF6A00] h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-black font-semibold">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-white text-black border-2 border-gray-300 focus:border-[#FF6A00] focus:ring-[#FF6A00] h-11 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FF6A00] hover:bg-[#FF5500] text-white font-bold text-lg h-12 shadow-lg hover:shadow-xl transition-all"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffLogin;
